import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme, alpha, keyframes } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import HeadsetIcon from "@mui/icons-material/Headset";
import CallEndIcon from "@mui/icons-material/CallEnd";

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 163, 127, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(16, 163, 127, 0); }
`;

const iceServers = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const VoiceChat = ({ socket, roomCode, players, compact = false }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isInVoiceChat, setIsInVoiceChat] = useState(false);
  const localAudioStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const audioElementsRef = useRef({});
  const theme = useTheme();

  useEffect(() => {
    if (!socket || !isInVoiceChat) return;

    const handleUserJoined = ({ userId }) => createPeerConnection(userId, true);
    const handleOffer = ({ from, offer }) => {
      const pc = createPeerConnection(from, false);
      pc.setRemoteDescription(new RTCSessionDescription(offer));
      pc.createAnswer().then((answer) => {
        pc.setLocalDescription(answer);
        socket.emit("webrtc-answer", { to: from, answer });
      });
    };
    const handleAnswer = ({ from, answer }) => {
      const pc = peerConnectionsRef.current[from];
      if (pc) pc.setRemoteDescription(new RTCSessionDescription(answer));
    };
    const handleIceCandidate = ({ from, candidate }) => {
      const pc = peerConnectionsRef.current[from];
      if (pc) pc.addIceCandidate(new RTCIceCandidate(candidate));
    };
    const handleUserLeft = ({ userId }) => {
      if (peerConnectionsRef.current[userId]) { peerConnectionsRef.current[userId].close(); delete peerConnectionsRef.current[userId]; }
      if (audioElementsRef.current[userId]) { audioElementsRef.current[userId].remove(); delete audioElementsRef.current[userId]; }
    };

    socket.on("user-joined-voice", handleUserJoined);
    socket.on("webrtc-offer", handleOffer);
    socket.on("webrtc-answer", handleAnswer);
    socket.on("webrtc-ice-candidate", handleIceCandidate);
    socket.on("user-left-voice", handleUserLeft);

    return () => {
      socket.off("user-joined-voice", handleUserJoined);
      socket.off("webrtc-offer", handleOffer);
      socket.off("webrtc-answer", handleAnswer);
      socket.off("webrtc-ice-candidate", handleIceCandidate);
      socket.off("user-left-voice", handleUserLeft);
    };
  }, [socket, isInVoiceChat]);

  const createPeerConnection = (remoteUserId, isInitiator) => {
    if (peerConnectionsRef.current[remoteUserId]) return peerConnectionsRef.current[remoteUserId];
    const pc = new RTCPeerConnection(iceServers);
    peerConnectionsRef.current[remoteUserId] = pc;
    localAudioStreamRef.current.getTracks().forEach((track) => pc.addTrack(track, localAudioStreamRef.current));

    pc.ontrack = (event) => {
      const remoteStream = event.streams[0];
      let audio = audioElementsRef.current[remoteUserId];
      if (!audio) { audio = document.createElement("audio"); audio.autoplay = true; audioElementsRef.current[remoteUserId] = audio; document.body.appendChild(audio); }
      audio.srcObject = remoteStream;
    };
    pc.onicecandidate = (event) => {
      if (event.candidate) socket.emit("webrtc-ice-candidate", { to: remoteUserId, candidate: event.candidate });
    };
    if (isInitiator) {
      pc.createOffer().then((offer) => { pc.setLocalDescription(offer); socket.emit("webrtc-offer", { to: remoteUserId, offer }); });
    }
    return pc;
  };

  const joinChat = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localAudioStreamRef.current = stream;
      stream.getAudioTracks()[0].enabled = false;
      setIsInVoiceChat(true);
      socket.emit("join-voice", { roomCode });
    } catch (error) {
      console.error("Microphone access denied:", error);
    }
  };

  const leaveChat = () => {
    socket.emit("leave-voice", { roomCode });
    if (localAudioStreamRef.current) localAudioStreamRef.current.getTracks().forEach((t) => t.stop());
    Object.values(peerConnectionsRef.current).forEach((pc) => pc.close());
    Object.values(audioElementsRef.current).forEach((audio) => audio.remove());
    peerConnectionsRef.current = {};
    audioElementsRef.current = {};
    localAudioStreamRef.current = null;
    setIsInVoiceChat(false);
  };

  const toggleMute = () => {
    if (localAudioStreamRef.current) {
      const track = localAudioStreamRef.current.getAudioTracks()[0];
      track.enabled = !track.enabled;
      setIsMuted(!track.enabled);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6, mr: 0.5 }}>Voice</Typography>
      {!isInVoiceChat ? (
        <Tooltip title="Join Voice Chat">
          <IconButton
            onClick={joinChat}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 2,
              "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
            }}
          >
            <HeadsetIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          <Tooltip title={isMuted ? "Unmute" : "Mute"}>
            <IconButton
              onClick={toggleMute}
              size="small"
              sx={{
                color: isMuted ? theme.palette.text.secondary : theme.palette.primary.main,
                border: `1px solid ${alpha(isMuted ? theme.palette.divider : theme.palette.primary.main, 0.4)}`,
                borderRadius: 2,
                animation: !isMuted ? `${pulse} 1.5s ease-in-out infinite` : "none",
              }}
            >
              {isMuted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Leave Voice">
            <IconButton
              onClick={leaveChat}
              size="small"
              sx={{
                color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
                "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.08) },
              }}
            >
              <CallEndIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default VoiceChat;
